/**
 * Chat Interface — Single-column conversational UI.
 * Per spec Section 4.5: clean, focused, no gamification.
 * Loads open session + previous closed sessions on mount.
 * Thread-aware: resets when threadId changes.
 * Previous sessions shown as collapsed expandable sections.
 * Current session messages shown inline with date grouping.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Loader2, ChevronDown, ChevronRight, Clock, Volume2, VolumeX } from 'lucide-react';
import { sendMessage, sendMessageStream, startSession, getOpenSession, listSessions, setVoiceDefaultOn } from '../lib/api';
import AssignmentCard from './AssignmentCard';
import UpgradePrompt from './UpgradePrompt';
import StateCheckIn from './StateCheckIn';
import DeepRestCard from './DeepRestCard';
import Nudge from './Nudge';
import { useNudge } from '../lib/hints';
import { useVoiceConversation } from '../lib/useVoiceConversation';
import VoiceInput from './VoiceInput';

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatSessionDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today at ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday at ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' at ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function groupMessagesByDate(messages) {
  const groups = [];
  const unstamped = [];
  const dated = {};

  for (const msg of messages) {
    if (!msg.timestamp) {
      unstamped.push(msg);
    } else {
      const dateKey = new Date(msg.timestamp).toLocaleDateString();
      if (!dated[dateKey]) {
        dated[dateKey] = { dateKey, label: formatDateLabel(msg.timestamp), messages: [] };
      }
      dated[dateKey].messages.push(msg);
    }
  }

  if (unstamped.length > 0) {
    groups.push({ dateKey: '_earlier', label: 'Earlier messages', messages: unstamped });
  }

  const sortedDated = Object.values(dated).sort(
    (a, b) => new Date(a.messages[0].timestamp) - new Date(b.messages[0].timestamp)
  );
  groups.push(...sortedDated);

  return groups;
}

function MessageBubble({ msg, dimmed }) {
  return (
    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
        msg.role === 'user'
          ? 'bg-blue-600/20 text-blue-100 border border-blue-800/30'
          : 'bg-slate-800/50 text-slate-200 border border-slate-700/50'
      } ${dimmed ? 'opacity-80' : ''}`}>
        {msg.thinking && !msg.content ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-xs">
              {msg.toolName ? `Running ${msg.toolName}...` : 'Thinking...'}
            </span>
          </div>
        ) : (
          <div className="whitespace-pre-wrap">{msg.content}</div>
        )}
      </div>
    </div>
  );
}

function DateGroup({ group, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 py-2 text-slate-500 text-xs font-medium hover:text-slate-400 transition-colors"
      >
        {expanded
          ? <ChevronDown size={14} />
          : <ChevronRight size={14} />
        }
        {group.label}
        <span className="text-slate-600">
          ({group.messages.length} messages)
        </span>
      </button>

      {expanded && (
        <div className="space-y-4">
          {group.messages.map((msg, i) => (
            <MessageBubble key={`${group.dateKey}-${i}`} msg={msg} dimmed={!defaultExpanded} />
          ))}
        </div>
      )}
    </div>
  );
}

function PreviousSession({ session }) {
  const [expanded, setExpanded] = useState(false);
  const messages = session.messages || [];
  const messageCount = messages.length;

  return (
    <div className="border border-slate-700/40 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-800/30 hover:bg-slate-800/50 transition-colors text-left"
      >
        {expanded
          ? <ChevronDown size={14} className="text-slate-500 flex-shrink-0" />
          : <ChevronRight size={14} className="text-slate-500 flex-shrink-0" />
        }
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Clock size={11} />
            <span>{formatSessionDate(session.started_at)}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">{messageCount} messages</span>
          </div>
          {session.summary_text && (
            <p className="text-xs text-slate-400 mt-1 truncate">{session.summary_text}</p>
          )}
        </div>
      </button>

      {expanded && messages.length > 0 && (
        <div className="px-4 py-3 space-y-3 border-t border-slate-700/30 bg-slate-900/20">
          {messages.map((msg, i) => (
            <MessageBubble key={`prev-${session.id}-${i}`} msg={msg} dimmed={true} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatInterface({ threadId, threadName, userState, activeAssignment, onSessionUpdate, onAssignmentCreated, onDataChange, voiceEnabled = false, voiceId, voiceDefaultOn = true, seedText, onSeedConsumed, lockedOut = false, canCreateRest = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => seedText || '');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [restoring, setRestoring] = useState(true);
  const [limitError, setLimitError] = useState(null);
  const areaNudge = useNudge('coach_area', !!threadId && !lockedOut);
  const [previousSessions, setPreviousSessions] = useState([]);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [entryScores, setEntryScores] = useState(null);
  const [evaluatedAssignment, setEvaluatedAssignment] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const voice = useVoiceConversation({
    enabled: voiceEnabled,
    defaultOn: voiceDefaultOn,
    onTranscribed: (text) => handleSendWithText(text),
    voiceId,
    onVoiceModeChange: (next) => { setVoiceDefaultOn(next).catch(() => {}); },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load open session + previous closed sessions for this thread
  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setPreviousSessions([]);
    setRestoring(true);
    setShowCheckIn(false);
    setEntryScores(null);

    (async () => {
      try {
        // Load both in parallel
        const [openRes, historyRes] = await Promise.all([
          getOpenSession(threadId),
          listSessions(threadId).catch(() => ({ sessions: [] }))
        ]);

        let openSessionId = null;
        let hasMessages = false;
        let hasExistingScores = false;
        if (openRes.session) {
          openSessionId = openRes.session.id;
          setSessionId(openSessionId);
          const saved = openRes.session.messages || [];
          hasMessages = saved.length > 0;
          hasExistingScores = !!openRes.session.entry_state_scores;
          if (hasMessages) {
            setMessages(saved);
          } else if (openRes.session.opener_text) {
            // Resumed session that had an opener but the user never typed —
            // re-render it as the first assistant bubble.
            setMessages([{
              role: 'assistant',
              content: openRes.session.opener_text,
              timestamp: openRes.session.started_at || new Date().toISOString(),
              synthetic: true,
            }]);
          }
        }

        // Filter out the open session and keep only closed sessions (chronological, oldest first)
        const closed = (historyRes.sessions || [])
          .filter(s => s.ended_at && s.id !== openSessionId)
          .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
        setPreviousSessions(closed);

        // Show check-in if: no messages in current session AND no existing scores.
        // A locked-out user (no paid subscription) skips it: /sessions will 403
        // into the trial prompt, and nobody should do a breathing exercise into
        // a paywall.
        if (!hasMessages && !hasExistingScores && !lockedOut) {
          setShowCheckIn(true);
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      } finally {
        setRestoring(false);
      }
    })();
  }, [threadId, lockedOut]);

  useEffect(() => {
    if (restoring) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // Grow to fit a pre-seeded message (e.g. opened from a pattern card).
    if (el.value) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [restoring]);

  // A seed message is consumed once on mount (the component is keyed by thread,
  // so it remounts per open); clear it upstream so it doesn't re-seed later.
  useEffect(() => {
    if (seedText && onSeedConsumed) onSeedConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateGroups = useMemo(() => groupMessagesByDate(messages), [messages]);
  const hasMultipleGroups = dateGroups.length > 1;

  // The backend auto-rotates a long session into a fresh one mid-conversation
  // (the old transcript is archived + summarized server-side). When that
  // happens it returns new_session_id; we must (1) point at the new session,
  // (2) collapse our local transcript to just the turn that carried over so we
  // don't re-send the whole archived array next turn, and (3) pull the
  // now-closed session into the "previous sessions" list.
  const handleRotation = useCallback(async (newId) => {
    setSessionId(newId);
    setMessages(prev => {
      const lastUserIdx = prev.map(m => m.role).lastIndexOf('user');
      return lastUserIdx >= 0 ? prev.slice(lastUserIdx) : prev;
    });
    try {
      const res = await listSessions(threadId);
      const closed = (res.sessions || [])
        .filter(s => s.ended_at && s.id !== newId)
        .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
      setPreviousSessions(closed);
    } catch {
      // Non-fatal: the archived session will appear on the next thread reload.
    }
  }, [threadId]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      const res = await startSession(threadId, entryScores);
      if (res.limit_type) {
        setLimitError(res);
        return null;
      }
      const newId = res.session.id;
      setSessionId(newId);
      // If the server generated an opener and we have no messages yet, show it
      // as a synthetic assistant bubble. Marked synthetic so we strip it from
      // the outgoing API payload (otherwise it would duplicate into context).
      if (res.session.opener_text) {
        setMessages(prev => prev.length === 0 ? [{
          role: 'assistant',
          content: res.session.opener_text,
          timestamp: res.session.started_at || new Date().toISOString(),
          synthetic: true,
        }] : prev);
        if (voice.voiceMode && voice.speak) {
          voice.speak(res.session.opener_text);
        }
      }
      return newId;
    } catch (err) {
      console.error('Failed to start session:', err);
      return null;
    }
  }, [sessionId, threadId, entryScores, voice]);

  // Eagerly create the session once the user is past StateCheckIn so the
  // opener can render BEFORE the user types. Without this, the opener would
  // only appear interleaved between the user's first message and the AI reply.
  useEffect(() => {
    if (restoring || sessionId || showCheckIn || limitError) return;
    ensureSession();
  }, [restoring, sessionId, showCheckIn, limitError, ensureSession]);

  const handleSendStreaming = async (updatedMessages, sid) => {
    let assistantText = '';
    const assistantMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString(), thinking: true };
    setMessages([...updatedMessages, assistantMsg]);
    setStreaming(true);
    voice.beginUtterance();

    let finalUsage = null;
    let assignmentCreated = null;
    let assignmentEvaluated = null;
    let newSessionId = null;

    // Synthetic messages (the opener) are display-only — never send them to
    // the model or they get duplicated into Anthropic's context.
    const apiMessages = updatedMessages.filter(m => !m.synthetic);

    await sendMessageStream(apiMessages, sid, (event) => {
      switch (event.type) {
        case 'text_delta':
          assistantText += event.text;
          voice.feedTextDelta(event.text);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText, thinking: false };
            return updated;
          });
          break;
        case 'tool_start':
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant' && !last.content) {
              updated[updated.length - 1] = { ...last, thinking: true, toolName: event.name };
            }
            return updated;
          });
          break;
        case 'tool_result':
          // Tool completed — text will follow in next iteration
          break;
        case 'done':
          finalUsage = event.usage;
          if (event.assignment_created) assignmentCreated = event.assignment_created;
          if (event.assignment_evaluated) assignmentEvaluated = event.assignment_evaluated;
          if (event.new_session_id) newSessionId = event.new_session_id;
          break;
        case 'error':
          assistantText += (assistantText ? '\n\n' : '') + `Error: ${event.message}`;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], content: assistantText };
            return updated;
          });
          break;
        default:
          break;
      }
    });

    // Close the voice utterance (flushes trailing sentence, ends playback queue)
    voice.endUtterance();

    // Post-stream handling
    if (newSessionId) await handleRotation(newSessionId);
    if (onSessionUpdate && finalUsage) onSessionUpdate(finalUsage);
    if (assignmentEvaluated) {
      setEvaluatedAssignment(assignmentEvaluated);
      if (onDataChange) onDataChange();
    }
    if (assignmentCreated) {
      setEvaluatedAssignment(null);
      if (onAssignmentCreated) onAssignmentCreated(assignmentCreated);
      if (onDataChange) onDataChange();
    }
  };

  const handleSendNonStreaming = async (updatedMessages, sid) => {
    const apiMessages = updatedMessages.filter(m => !m.synthetic);
    const result = await sendMessage(apiMessages, sid);

    if (result.response) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString()
      }]);
      if (result.new_session_id) await handleRotation(result.new_session_id);
      if (onSessionUpdate && result.usage) onSessionUpdate(result.usage);
      if (result.assignment_evaluated) {
        setEvaluatedAssignment(result.assignment_evaluated);
        if (onDataChange) onDataChange();
      }
      if (result.assignment_created && onAssignmentCreated) {
        setEvaluatedAssignment(null);
        onAssignmentCreated(result.assignment_created);
      }
      if (result.assignment_created && onDataChange) onDataChange();
    } else if (result.error) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: `Error: ${result.error}`,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleSendWithText = async (text) => {
    if (!text || !text.trim() || loading) return;

    const sid = await ensureSession();
    if (!sid) return;

    const userMsg = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setLoading(true);

    try {
      await handleSendStreaming(updatedMessages, sid);
    } catch (streamErr) {
      // Streaming failed — fall back to non-streaming
      console.warn('Streaming failed, falling back:', streamErr.message);
      try {
        await handleSendNonStreaming(updatedMessages, sid);
      } catch (err) {
        setMessages([...updatedMessages, {
          role: 'assistant',
          content: 'Connection error. Please try again.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const handleSend = () => handleSendWithText(input.trim());

  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    autoGrow(e.target);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full max-w-3xl mx-auto">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-4 space-y-4">
        {/* Assignment card */}
        <AssignmentCard assignment={activeAssignment} evaluatedAssignment={evaluatedAssignment} />
        {areaNudge.show && (
          <Nudge title="One coach, many areas." text="Each life area is its own conversation with its own memory. The card below can make a Deep Rest for just this area." onDismiss={areaNudge.dismiss} />
        )}
        {/* Deep Rest scoped to this life area (paid; the API 403s otherwise) */}
        {!restoring && !showCheckIn && !lockedOut && threadId && (
          <DeepRestCard threadId={threadId} threadName={threadName || 'this area'} canCreate={canCreateRest} className="mb-2" />
        )}
        {restoring ? (
          <div className="text-center text-slate-500 mt-12">
            <Loader2 size={20} className="animate-spin text-slate-400 mx-auto" />
          </div>
        ) : showCheckIn ? (
          <StateCheckIn
            onComplete={(scores) => {
              setEntryScores(scores);
              setShowCheckIn(false);
            }}
            onSkip={() => {
              setShowCheckIn(false);
            }}
          />
        ) : (
          <>
            {/* Previous closed sessions — expandable */}
            {previousSessions.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Previous Sessions</p>
                {previousSessions.map(session => (
                  <PreviousSession key={session.id} session={session} />
                ))}
                {messages.length > 0 && (
                  <div className="border-t border-slate-700/30 mt-4 pt-2">
                    <p className="text-xs text-slate-600 font-medium uppercase tracking-wide">Current Session</p>
                  </div>
                )}
              </div>
            )}

            {/* Current session messages */}
            {messages.length === 0 && previousSessions.length === 0 ? (
              <div className="text-center text-slate-500 mt-12">
                <p className="text-lg font-medium text-slate-400 mb-2">The Navigator</p>
                <p className="text-sm">Tell me what you're working through. What brought you here?</p>
              </div>
            ) : messages.length === 0 && previousSessions.length > 0 ? (
              <div className="text-center text-slate-500 mt-6">
                <p className="text-sm">Continue the conversation below.</p>
              </div>
            ) : hasMultipleGroups ? (
              dateGroups.map((group, gi) => (
                <DateGroup
                  key={group.dateKey}
                  group={group}
                  defaultExpanded={gi === dateGroups.length - 1}
                />
              ))
            ) : (
              messages.map((msg, i) => (
                <MessageBubble key={`msg-${i}`} msg={msg} dimmed={false} />
              ))
            )}
          </>
        )}

        {loading && !streaming && (
          <div className="flex justify-start">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3">
              <Loader2 size={16} className="animate-spin text-slate-400" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {voice.error && (
        <div className="px-4 py-2 bg-red-900/20 border-t border-red-900/40 text-xs text-red-300 flex items-center justify-between">
          <span>{voice.error}</span>
          <button onClick={voice.clearError} className="text-red-400 hover:text-red-200" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-slate-800 px-3 sm:px-4 pt-3 pb-safe-2">
        {limitError ? (
          <div className="max-w-3xl mx-auto">
            <UpgradePrompt limitError={limitError} onDismiss={() => setLimitError(null)} />
          </div>
        ) : voice.voiceMode ? (
          <VoiceInput voice={voice} loading={loading} />
        ) : (
          <div className="flex gap-2 max-w-3xl mx-auto items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-500 resize-none overflow-hidden focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {voice.available && (
              <button
                onClick={voice.toggleVoiceMode}
                title="Switch to voice mode"
                aria-label="Switch to voice mode"
                className="text-slate-500 hover:text-purple-300 hover:bg-purple-900/20 rounded-lg p-3 min-w-tap min-h-tap sm:min-w-0 sm:min-h-0 sm:w-11 sm:h-11 flex items-center justify-center transition-colors"
              >
                <VolumeX size={16} />
              </button>
            )}
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              aria-label="Send message"
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg px-4 py-3 transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
